import { BrainIcon, HeartHandshake, LucideIcon } from 'lucide-react';

interface Route {
  name: string;
  path: string;
  icon?: LucideIcon;
  customIcon?: React.ReactNode;
}

// Only the new app router routes should be added here
export enum ROUTES {
  // Login
  login = 'login',
  home = 'home',

  // Wellness
  wellness = 'wellness',

  // Mindset
  mindset = 'mindset',
  mindsetEducation = 'mindset-education',
  mindsetPills = 'mindset-education-pills',
  mindsetHabit = 'mindset-habit',
  mindsetCoach = 'mindset-habit-coach-consultId',

  // Onboarding
  onboarding = 'onboarding',
  onboardingSuccess = 'onboarding-success',

  // Rehab
  rehab = 'rehab',

  // Session
  sessionPlayer = 'sessionPlayer',
  sessionPresession = 'sessionPresession',

  // Profile
  profile = 'me',
  profileStats = 'me-stats',
  profileStatsCalendar = 'me-stats-calendar',
  profileStatsHistory = 'me-stats-history',
  profileCommunity = 'me-community',
  profileSettings = 'me-settings',

  // Chat
  chat = 'chat',

  // Error test
  errorTest = 'error-test',
}

// Only the new app router routes should be added here
export const routes: Route[] = [
  // Login
  {
    name: ROUTES.login,
    path: '/login',
  },

  { name: ROUTES.home, path: '/home' },
  // Wellness
  {
    name: ROUTES.wellness,
    path: '/wellness',
    icon: HeartHandshake,
  },

  // Mindset
  {
    name: ROUTES.mindset,
    path: '/mindset',
    icon: BrainIcon,
  },
  {
    name: ROUTES.mindsetEducation,
    path: '/mindset/education',
  },
  {
    name: ROUTES.mindsetPills,
    path: '/mindset/education/pills',
  },
  {
    name: ROUTES.mindsetHabit,
    path: '/mindset/habit',
  },
  {
    name: ROUTES.mindsetCoach,
    path: '/mindset/habit/coach/:consultId',
  },

  // Onboarding
  {
    name: ROUTES.onboarding,
    path: '/onboarding',
  },
  {
    name: ROUTES.onboardingSuccess,
    path: '/onboarding/success',
  },

  // Rehab
  { name: ROUTES.rehab, path: '/rehab' },

  // Session
  { name: ROUTES.sessionPlayer, path: '/session/:sessionKey/player' },
  { name: ROUTES.sessionPresession, path: '/session/:sessionKey/presession' },

  // Profile
  { name: ROUTES.profile, path: '/me' },
  { name: ROUTES.profileStats, path: '/me/stats' },
  { name: ROUTES.profileStatsCalendar, path: '/me/stats/calendar' },
  { name: ROUTES.profileStatsHistory, path: '/me/stats/history' },
  { name: ROUTES.profileCommunity, path: '/me/community' },
  { name: ROUTES.profileSettings, path: '/me/settings' },

  // Chat
  { name: ROUTES.chat, path: '/chat' },

  // Error test
  { name: ROUTES.errorTest, path: '/error-test' },
];

interface RouteOption {
  [key: string]: string | undefined;
}
interface RouteOptions {
  params?: RouteOption[];
  query?: RouteOption[];
}

/**
 * Retrieves a route object by its name, with optional route options.
 * @param name - The name of the route to retrieve.
 * @param options - Optional route options.
 * @returns The route object that matches the given name and options, or the first route if no match is found.
 */
export function getPathByName(name: string, options?: RouteOptions) {
  const route = routes.find((el) => el.name === name) ?? routes[0];

  if (!route) return route;

  let path = route.path;

  if (options?.params) {
    path = replaceParams(path, options.params);
  }

  if (options?.query) {
    path = appendQuery(path, options.query);
  }

  return { ...route, path };
}

function replaceParams(path: string, params: RouteOption[]): string {
  return params.reduce((acc, param) => {
    const [key, value] = Object.entries(param)[0];
    return value ? acc.replace(`:${key}`, value) : acc;
  }, path);
}

function appendQuery(path: string, query: RouteOption[]): string {
  const queryString = query
    .map((param) => {
      const [key, value] = Object.entries(param)[0];
      return `${key}=${value}`;
    })
    .join('&');

  return queryString ? `${path}?${queryString}` : path;
}
